import { getAction, loadRules, findWinnersToPromote } from './performanceRules.js';
import { parseNomenclature, studioFeatureKey, STUDIO_FEATURES } from './nomenclatureParser.js';

// Lost-potential model — defendable estimate, editable via timbo_perf_rules.lostPotential.
//   sum(winner test purchases) × survivalRate × scaleMultiple × ltv
// Defaults are conservative (18% of promoted winners actually scale, 10× volume, €8 LTV)
// instead of the old uniform 50× that over-counted every winner.
const DEFAULT_LOST_POTENTIAL = { survivalRate: 0.18, scaleMultiple: 10, ltv: 8 };

function phaseFromCategory(cat) {
  // v3.99.5 Fix 4: permissive matching + console.warn diagnostic for unmapped categories.
  if (!cat || typeof cat !== 'string') return 'other';
  const c = cat.trim().toUpperCase();
  if (c === 'TEST' || c === 'TESTING' || c === 'T' || c.includes('TEST')) return 'testing';
  if (['MAIN', 'BIS', 'ITERATION', 'M'].includes(c) || c.includes('MAIN') || c.includes('BIS') || c.includes('ITER')) return 'main';
  // Diagnostic: log unique uncategorized values once (browser only). Remove after v3.99.6 audit.
  if (typeof window !== 'undefined') {
    if (!window.__loggedPhases) window.__loggedPhases = new Set();
    if (!window.__loggedPhases.has(c)) {
      window.__loggedPhases.add(c);
      console.warn('[phase] Unmapped adset_category: "' + cat + '" — treating as \'other\'');
    }
  }
  return 'other';
}

function classifyV2(ad, phase, isPromoted, rules) {
  const nom = parseNomenclature(ad.ad_name_sample);
  return getAction(ad, rules, { phase, isPromoted, lang: nom.lang }).action;
}

/** Filter ads whose last_date falls within the period window.
 *  Accepts a preset string ('1d'..'30d') OR an object { since, until } (YYYY-MM-DD). */
export function filterByPeriod(ads, period) {
  if (!ads || !ads.length) return [];
  // Object input: filter ads whose last_date falls in [since, until]
  if (period && typeof period === 'object' && period.since && period.until) {
    const { since, until } = period;
    return ads.filter(ad => {
      // v4.00 — overlap sur la période réelle [first_date, last_date].
      // Aucune date => aucune activité dans aucune fenêtre => exclue.
      if (!ad.last_date && !ad.first_date) return false;
      const adStart = ad.first_date || ad.last_date;
      const adEnd = ad.last_date || ad.first_date;
      return adStart <= until && adEnd >= since;
    });
  }
  // String preset input (legacy + SendToUAModal usage)
  const days = { '1d': 1, '3d': 3, '7d': 7, '14d': 14, '30d': 30 }[period] || 30;
  const now = new Date();
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  return ads.filter(ad => {
    if (!ad.last_date) return false; // v4.00 — no activity => excluded from windowed views
    return ad.last_date >= cutoffStr;
  });
}

/**
 * Compute all Overview KPIs from V2 aggregated ads.
 * Returns: { spend, todo, perfByFeature, enrichedAds }
 */
export function calculateOverviewKPIs(v2AggAds, period) {
  if (!v2AggAds || !v2AggAds.length) return null;

  const filtered = period ? filterByPeriod(v2AggAds, period) : v2AggAds;
  if (!filtered.length) return null;
  const rules = loadRules();

  // Build core_id -> ads map to detect promotion status
  const byCoreId = new Map();
  for (const ad of filtered) {
    if (!ad.core_id) continue;
    if (!byCoreId.has(ad.core_id)) byCoreId.set(ad.core_id, []);
    byCoreId.get(ad.core_id).push(ad);
  }

  const promotedCoreIds = new Set();
  for (const [coreId, ads] of byCoreId.entries()) {
    if (ads.some(a => ['MAIN', 'BIS', 'ITERATION'].includes(a.adset_category))) {
      promotedCoreIds.add(coreId);
    }
  }

  // Classify each ad and accumulate
  let totalSpend = 0, totalWaste = 0, liveCount = 0;
  let promoteN = 0, iterateN = 0, fatigueN = 0, localizeN = 0;
  const byFeature = {};
  STUDIO_FEATURES.forEach(f => {
    byFeature[f.key] = { spend: 0, testingTotal: 0, testingWinners: 0, mainTotal: 0, mainHealthy: 0 };
  });

  const enrichedAds = filtered.map(ad => {
    const phase = phaseFromCategory(ad.adset_category);
    const isPromoted = promotedCoreIds.has(ad.core_id);
    const action = classifyV2(ad, phase, isPromoted, rules);
    const nom = parseNomenclature(ad.ad_name_sample);
    const feat = studioFeatureKey(nom.feature);

    totalSpend += ad.spend;
    if (ad.delivery && ad.delivery.toLowerCase().includes('activ')) liveCount++;
    if (action === 'CUT' || action === 'FATIGUE') totalWaste += ad.spend;
    if (action === 'PROMOTE') promoteN++;
    if (action === 'ITERATE') iterateN++;
    if (action === 'FATIGUE') fatigueN++;
    if (action === 'LOCALIZE') localizeN++;

    if (feat && byFeature[feat]) {
      byFeature[feat].spend += ad.spend;
      if (phase === 'testing') {
        byFeature[feat].testingTotal++;
        if (action === 'PROMOTE') byFeature[feat].testingWinners++;
      }
      if (phase === 'main') {
        byFeature[feat].mainTotal++;
        if (['HEALTHY', 'ITERATE', 'LOCALIZE'].includes(action)) byFeature[feat].mainHealthy++;
      }
    }

    return { ...ad, phase, action, nomenclature: nom, feature: feat };
  });

  const totalFeatureSpend = Object.values(byFeature).reduce((s, x) => s + x.spend, 0) || 1;
  const perfByFeature = STUDIO_FEATURES.map(feat => {
    const d = byFeature[feat.key];
    const spendPct = d.spend / totalFeatureSpend * 100;
    const hasFeatData = d.spend > 0;
    let winners = 0, total = 0, hitLabel = 'no data';
    if (d.testingTotal > 0) { winners = d.testingWinners; total = d.testingTotal; hitLabel = 'winners'; }
    else if (d.mainTotal > 0) { winners = d.mainHealthy; total = d.mainTotal; hitLabel = 'healthy'; }
    return { name: feat.label, key: feat.key, spend: d.spend, spendPct, hasFeatData, winners, total, hitLabel, d };
  }).sort((a, b) => b.spend - a.spend);

  return {
    spend: {
      total: Math.round(totalSpend),
      waste: Math.round(totalWaste),
      creativesLive: liveCount,
    },
    todo: {
      promote: promoteN,
      iterate: iterateN,
      fatigue: fatigueN,
      localize: localizeN,
    },
    perfByFeature,
    enrichedAds,
    total: filtered.length,
  };
}

export function calculateKPIs(aggregatedAds) {
  const winnersToPromote = findWinnersToPromote(aggregatedAds);

  const byCoreId = new Map();
  for (const ad of aggregatedAds) {
    if (!ad.core_id) continue;
    if (!byCoreId.has(ad.core_id)) byCoreId.set(ad.core_id, []);
    byCoreId.get(ad.core_id).push(ad);
  }

  const rules = loadRules();
  let promotedCount = 0, testWinnersCount = 0;
  for (const ads of byCoreId.values()) {
    const wasPromoted = ads.some(a => ['MAIN', 'BIS', 'ITERATION'].includes(a.adset_category));
    // v2: per-run threshold — judge the best TEST run (max purchases), never an arbitrary one.
    const testAd = ads.filter(a => a.adset_category === 'TEST')
      .reduce((best, a) => (best && (best.achats || 0) >= (a.achats || 0)) ? best : a, null);
    const lang = testAd ? parseNomenclature(testAd.ad_name_sample).lang : null;
    const wasWinnerInTest = testAd && getAction(testAd, rules, { phase: 'testing', lang }).action === 'PROMOTE';
    if (wasWinnerInTest) testWinnersCount++;
    if (wasPromoted && wasWinnerInTest) promotedCount++;
  }

  const promotionRate = testWinnersCount > 0 ? Math.round((promotedCount / testWinnersCount) * 100) : 0;
  const totalOrphanAchats = winnersToPromote.reduce((sum, ad) => sum + ad.achats, 0);
  const lp = (rules && rules.lostPotential) || DEFAULT_LOST_POTENTIAL;
  const manqueAGagnerEUR = Math.round(totalOrphanAchats * lp.survivalRate * lp.scaleMultiple * lp.ltv);
  const totalSpend = Math.round(aggregatedAds.reduce((sum, ad) => sum + ad.spend, 0));
  const wasteSpend = Math.round(aggregatedAds
    .filter(a => {
      const phase = phaseFromCategory(a.adset_category);
      const lang = parseNomenclature(a.ad_name_sample).lang;
      const act = getAction(a, rules, { phase, lang }).action;
      return act === 'CUT' || act === 'FATIGUE';
    })
    .reduce((s, ad) => s + ad.spend, 0));

  return {
    totalWinnersToPromote: winnersToPromote.length,
    manqueAGagnerEUR,
    promotionRate,
    totalSpend,
    wasteSpend,
    creativesLive: aggregatedAds.filter(a => a.delivery === 'active').length,
    winnersToPromote,
  };
}
